import {SessionContext, SessionContextInterface} from "./SessionContext";
import {useSessionStorage} from "usehooks-ts";
import {Api} from "./Api";
import {useEffect} from "react";
import {useImmer} from "use-immer";
import {AppToaster} from "./AppToaster";

export const SessionProvider = (props: { children: React.ReactNode }) => {
    const [userId, setUserId] = useSessionStorage("userId", "");
    const [userData, setUserData] = useImmer(null);

    useEffect(() => {
        if (userId && userData === null) {
            console.log("Initializing user data for userId:", userId);
            Api.getUserData(userId).then((data: any) => {
                setUserData(data);
            }).catch((error) => {
                console.error("Error initializing user data:", error);
            });
        }
    }, [setUserData, userData, userId]);

    const login = async (userId: string) => Api.login(userId).then(() => setUserId(userId));

    const logout = () => {
        setUserId("");
        setUserData(null);
    };

    const writeUserData = async (data: any) => {
        Api.writeUserData(userId, data).then(() => {
            if (typeof data === "string") {
                throw new Error("writeUserData expects an object, not a string");
            }
            setUserData(data);
        }).catch((error) => {
            AppToaster.show({
                message: "Error writing user data: " + error.message,
                intent: "danger",
                timeout: 5000
            })
            console.error("Error writing user data:", error);
        })
    }

    const getGroupData = async () => {
        if (!userId) {
            throw new Error("User not logged in, cannot get group data");
        }
        return Api.getGroupData(userId).then((data: any) => {
            return data;
        }).catch((error) => {
            AppToaster.show({
                message: "Error fetching group data: " + error.message,
                intent: "danger",
                timeout: 5000
            });
            console.error("Error fetching group data:", error);
            return null;
        });
    }

    const getGlobalData = async () => {
        if (!userId) {
            throw new Error("User not logged in, cannot get global data");
        }
        return Api.getGlobalData(userId).then((data: any) => {
            return data;
        }).catch((error) => {
            AppToaster.show({
                message: "Error fetching global data: " + error.message,
                intent: "danger",
                timeout: 5000
            });
            console.error("Error fetching global data:", error);
            return null;
        });
    }

    const contextValue: SessionContextInterface = {
        isAuthenticated: !!userId,
        userId,
        login,
        logout,
        userData,
        getGroupData,
        writeUserData,
        getGlobalData
    }

    return <SessionContext.Provider value={contextValue}>
        {props.children}
    </SessionContext.Provider>
}